import { Routes } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from './core/cookie.service';
import { isPlatformServer } from '@angular/common';
import {
  decodePortalUserTypesCookie,
  PORTAL_USER_TYPE_COOKIE_KEY,
} from './core/auth/portal-user-type';
import { AuthTokenService } from './core/auth/auth-token.service';
import {
  COMPANY_ONBOARDING_PENDING_COOKIE_KEY,
  decodeCompanyOnboardingPendingCookie,
} from './core/auth/company-onboarding';
// NOTE: initial home redirects are handled by PortalHomeRedirect component.

export const authGuard = () => {
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  const token = inject(AuthTokenService).getToken();

  // SERVER CHECK
  if (isPlatformServer(platformId)) {
    // If server sees token, great. 
    // If NOT, we often return true anyway to let the browser 
    // double-check before doing a hard redirect.
    if (token) return true;
    
    // To prevent the "Not Working" loop, allow the server to pass 
    // and let the client-side guard do the final check.
    return true; 
  }

  // BROWSER CHECK
  if (token) {
    return true;
  }

  // No token: reroute to login
  router.navigate(['/auth/login']);
  return false;
};

export const onboardingCompleteGuard = () => {
  const router = inject(Router);
  const cookies = inject(CookieService);

  const pending = decodeCompanyOnboardingPendingCookie(cookies.getItem(COMPANY_ONBOARDING_PENDING_COOKIE_KEY));
  if (!pending) {
    return true;
  }

  const portalUserTypes = decodePortalUserTypesCookie(cookies.getItem(PORTAL_USER_TYPE_COOKIE_KEY));
  const isCompanyAdmin = portalUserTypes?.office === 1 || portalUserTypes?.office === 2;
  router.navigate([isCompanyAdmin ? '/auth/welcome' : '/auth/login']);
  return false;
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./core/auth/portal-home-redirect').then(m => m.PortalHomeRedirect),
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routing').then(m => m.AUTH_ROUTES)
  },
  {
    path: 'legal',
    loadChildren: () => import('./legal/legal.routes').then(m => m.LEGAL_ROUTES),
  },
  {
    path: 'terms-of-use',
    redirectTo: 'legal/terms-of-use',
    pathMatch: 'full',
  },
  {
    path: 'privacy-policy',
    redirectTo: 'legal/privacy-policy',
    pathMatch: 'full',
  },
  {
    path: 'office',
    loadChildren: () => import('./office/office.routing').then(m => m.OFFICE_ROUTES),
    canActivate: [authGuard, onboardingCompleteGuard]
  },
  {
    path: 'property/terms-of-use',
    redirectTo: 'legal/terms-of-use',
    pathMatch: 'full',
  },
  {
    path: 'property/privacy-policy',
    redirectTo: 'legal/privacy-policy',
    pathMatch: 'full',
  },
  {
    path: 'property',
    loadChildren: () => import('./property/property.routing').then(m => m.PROPERTY_ROUTES),
    canActivate: [authGuard, onboardingCompleteGuard]
  },
  // Wildcard route for a 404 page (optional: create a NotFoundComponent or similar)
  {
    path: '**',
    loadComponent: () => import('./components/not-found/not-found.component').then(m => m.NotFoundComponent)
  }
];
